/**
 * What the ticket is, beside what is being said about it.
 *
 * The panel down the right of the ticket page, as legacy had it: the facts at
 * the top and the controls that change the ticket at the bottom, so the desk
 * can move a ticket on without opening a form. Each control saves on change
 * and the chat records what it did — the person driving this panel never has
 * to also write down what they just did, which is what legacy asked of them.
 *
 * Every save is a PATCH of the one field being changed, carrying the ticket as
 * it was last read — so one control saves on its own, and two people driving
 * this panel at once cannot silently overwrite each other.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { Check, Copy } from "lucide-react";
import { type ReactNode, useState } from "react";
import { toast } from "sonner";

import { AttachmentList } from "@/components/helpdesk/attachments";
import { Badge } from "@/components/ui/badge";
import { Button, IconButton } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { OptionSelect } from "@/components/ui/option-select";
import { FkSelect } from "@/components/ui/fk-select";
import { api, errorMessage, ifMatch } from "@/services/api";
import {
  ASSIGNMENT_LABEL,
  ASSIGNMENTS,
  DEPARTMENT_LABEL,
  HELPDESK_KEY,
  TICKETS_ENDPOINT,
  isClosed,
  PRIORITIES,
  PRIORITY_LABEL,
  STATUS_LABEL,
  STATUS_TONE,
  STATUSES,
  type Ticket,
  type TicketAssignment,
  type TicketPriority,
  type TicketStatus,
  TYPE_LABEL,
} from "@/services/helpdesk";
import { useCan } from "@/utils/permissions";
import { useTranslation } from "@/store/locale";

/**
 * What a ticket may be moved to, and what a closed one may.
 *
 * A closed ticket offers only Reopen, because that is the one change the API
 * takes on one — offering the other six would be six ways to be told no. An
 * open one is not offered Reopen, which would mean nothing.
 */
const movesTo = (status: TicketStatus): TicketStatus[] =>
  isClosed(status)
    ? ["reopen"]
    : STATUSES.filter((one) => one !== "reopen" || status === "reopen");

export function TicketInfo({ ticket }: { ticket: Ticket }) {
  const t = useTranslation();
  const queryClient = useQueryClient();
  const allow = useCan();
  const [copied, setCopied] = useState(false);
  /**
   * Which bucket the panel is showing, which is not always the ticket's own.
   *
   * Picking "an employee" is a step towards assigning rather than an assignment
   * — there is nobody named yet — so it lives here until a name is chosen. Any
   * other pick saves at once and the ticket catches up, which is why the state
   * is re-seeded whenever the ticket changes underneath.
   */
  const [picked, setPicked] = useState<TicketAssignment | null>(null);
  const [seen, setSeen] = useState(ticket.assignment);
  if (seen !== ticket.assignment) {
    setSeen(ticket.assignment);
    setPicked(null);
  }
  const bucket = picked ?? ticket.assignment;
  const setBucket = setPicked;

  const canChange = allow("change__helpdesk_ticket");
  const closed = isClosed(ticket.status);

  const change = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      (await api.patch<Ticket>(`/helpdesk/tickets/${ticket.id}`, body, ifMatch(ticket))).data,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: HELPDESK_KEY });
      await queryClient.invalidateQueries({ queryKey: [TICKETS_ENDPOINT] });
    },
    onError: (failure) => toast.error(errorMessage(failure, t("hd.saveFailed"))),
  });

  const busy = change.isPending;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(ticket.reference);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      toast.error(t("hd.copyFailed"));
    }
  };

  return (
    <aside className="rounded-card bg-surface-raised flex flex-col gap-4 p-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-title-3 text-heading">{t("hd.ticketInformation")}</h3>
        {canChange ? (
          <Button
            variant="ghost"
            loading={busy}
            onClick={() => change.mutate({ status: closed ? "reopen" : "resolved" })}
          >
            {closed ? t("hd.reopenTicket") : t("hd.closeTicket")}
          </Button>
        ) : null}
      </div>

      <dl className="grid gap-3">
        <Row label={t("hd.reference")}>
          <span className="flex items-center gap-1.5">
            <span className="font-mono">{ticket.reference}</span>
            <IconButton
              label={t("hd.copyReference")}
              size="icon-sm"
              onClick={() => void copy()}
            >
              {copied ? <Check aria-hidden /> : <Copy aria-hidden />}
            </IconButton>
          </span>
        </Row>
        <Row label={t("hd.title")}>{ticket.title}</Row>
        <Row label={t("hd.description")}>
          <span className="whitespace-pre-wrap">{ticket.description}</span>
        </Row>
        <Row label={t("hd.department")}>
          {ticket.department ? t(DEPARTMENT_LABEL[ticket.department]) : "—"}
        </Row>
        <Row label={t("hd.type")}>
          {ticket.ticket_type ? t(TYPE_LABEL[ticket.ticket_type]) : "—"}
        </Row>
        <Row label={t("hd.dueDate")}>
          {ticket.due_date ? format(new Date(ticket.due_date), "MMM d, yyyy") : "—"}
        </Row>
        <Row label={t("hd.raisedBy")}>{ticket.created_by_name ?? "—"}</Row>
        <Row label={t("hd.raisedOn")}>
          {format(new Date(ticket.created_at), "MMM d, yyyy h:mm a")}
        </Row>
        {ticket.attachments.length > 0 ? (
          <Row label={t("hd.attachments")}>
            {/* One, and a count for the rest: this is a narrow column beside
                the conversation, and a stack of thumbnails would push what the
                panel is for off the screen. The count opens the gallery. */}
            <AttachmentList files={ticket.attachments} show={1} />
          </Row>
        ) : null}
      </dl>

      {canChange ? (
        <div className="border-line flex flex-col gap-3 border-t pt-4">
          <Field label={t("hd.status")} htmlFor="ticket-status">
            <OptionSelect
              id="ticket-status"
              fieldSize="sm"
              value={ticket.status}
              disabled={busy}
              onChange={(next) => change.mutate({ status: next })}
              choices={movesTo(ticket.status).map((value) => ({
                value,
                label: t(STATUS_LABEL[value]),
              }))}
            />
          </Field>

          <Field label={t("hd.priority")} htmlFor="ticket-priority-field">
            <OptionSelect
              id="ticket-priority-field"
              fieldSize="sm"
              value={ticket.priority}
              // A closed ticket's priority is a question nobody is asking any
              // more; the only control still live on one is the status.
              disabled={closed || busy}
              onChange={(next) => change.mutate({ priority: next as TicketPriority })}
              choices={PRIORITIES.map((value) => ({
                value,
                label: t(PRIORITY_LABEL[value]),
              }))}
            />
          </Field>

          <Field label={t("hd.assignTo")} htmlFor="ticket-assignment">
            <OptionSelect
              id="ticket-assignment"
              fieldSize="sm"
              value={bucket}
              disabled={closed || busy}
              onChange={(next) => {
                const chosen = next as TicketAssignment;
                setBucket(chosen);
                // "An employee" is not a state a ticket can be in on its own —
                // the API refuses it without a name, and rightly: a ticket
                // assigned to nobody in particular is the `unassigned` it
                // already was. So this choice only opens the picker below, and
                // the save happens when somebody is actually named.
                if (chosen === "employee") return;
                change.mutate({ assignment: chosen, assignee_id: null });
              }}
              choices={ASSIGNMENTS.map((value) => ({
                value,
                label: t(ASSIGNMENT_LABEL[value]),
              }))}
            />
          </Field>

          {/* The person, once the bucket is one that has one — from the desk's
              own roster, which is not the list of everybody who signs in. */}
          {bucket === "employee" ? (
            <Field
              label={t("hd.handler")}
              htmlFor="ticket-assignee"
              {...(ticket.assignee_id ? {} : { hint: t("hd.pickHandler") })}
            >
              <FkSelect
                id="ticket-assignee"
                endpoint="/helpdesk/employees/compact"
                value={ticket.assignee_id ? String(ticket.assignee_id) : ""}
                disabled={closed || busy}
                placeholder={t("hd.selectHandler")}
                // Naming somebody is what assigns the ticket: the API settles
                // the bucket from the name, so the screen sends the one fact
                // it has rather than two that have to agree.
                onChange={(next) =>
                  next
                    ? change.mutate({ assignee_id: Number(next) })
                    : change.mutate({ assignment: "unassigned", assignee_id: null })
                }
              />
            </Field>
          ) : null}
        </div>
      ) : (
        // Without the right to change it, the same three facts as facts.
        <dl className="border-line grid gap-3 border-t pt-4">
          <Row label={t("hd.status")}>
            <Badge tone={STATUS_TONE[ticket.status]}>{t(STATUS_LABEL[ticket.status])}</Badge>
          </Row>
          <Row label={t("hd.priority")}>{t(PRIORITY_LABEL[ticket.priority])}</Row>
          <Row label={t("hd.assignTo")}>
            {ticket.assignee_name ?? t(ASSIGNMENT_LABEL[ticket.assignment])}
          </Row>
        </dl>
      )}
    </aside>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[minmax(6rem,38%)_1fr] items-start gap-3">
      <dt className="text-fg-subtle text-subhead">{label}</dt>
      <dd className="text-fg text-body min-w-0 break-words">{children}</dd>
    </div>
  );
}
